import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PaymentForm from './PaymentForm';
import { Paper, Box, Typography, CircularProgress, Chip } from '@mui/material';
import { Users } from 'lucide-react';
import './payment.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Payment = ({ reviewData, onBack }) => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Get number of lines from reviewData
  const numLines = reviewData?.userDetails?.numLines || 1;
  
  // Calculate the total due today instead of just the monthly amount
  const calculateTotalAmount = () => {
    if (!reviewData) return 0;
    
    // Extract values, defaulting to 0 if undefined
    const monthlyRate = Number(reviewData.planDetails?.monthlyRate || 0);
    const taxes = Number(reviewData.planDetails?.taxes || 0);
    const activationFee = Number(reviewData.planDetails?.totalActivationFee || 0);
    const subscriptionCost = Number(reviewData.subscriptionDetails?.totalSubscriptionCost || 0);
    
    // Determine if eSIM
    const isEsim = reviewData?.userDetails?.hasEsim || reviewData?.userDetails?.esim || false;
    
    // Calculate shipping cost based on method
    const isExpedited = reviewData.subscriptionDetails?.shipping === 'expedited';
    const shippingCost = isEsim ? 0 : (isExpedited ? 15.00 : 8.00);
    
    // Calculate totals
    const verizonTotal = monthlyRate + taxes + activationFee;
    const circledinTotal = subscriptionCost + shippingCost;
    
    return verizonTotal + circledinTotal;
  };
  
  // Get total amount in cents for Stripe
  const totalAmount = Math.round(calculateTotalAmount() * 100);

  // Initial options for Elements
  const options = {
    mode: 'subscription',
    amount: totalAmount,
    currency: 'usd',
    paymentMethodTypes: ['card'],
    paymentMethodOrder: ['card'],
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#FF4775',
        colorBackground: '#ffffff',
        colorText: '#111827',
        colorDanger: '#EF4444',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '8px',
        spacingUnit: '4px',
      },
      rules: {
        '.Tab': {
          border: '1px solid #E5E7EB',
          boxShadow: 'none !important',
          backgroundColor: 'transparent !important',
          transition: 'none !important'
        },
        '.Tab:hover': {
          color: '#FF4775',
          border: '1px solid #E5E7EB !important',
          boxShadow: 'none !important',
          backgroundColor: 'transparent !important'
        },
        '.Tab:focus': {
          boxShadow: 'none !important',
          border: '1px solid #FF4775 !important'
        },
        '.Tab--selected': {
          borderColor: '#FF4775 !important',
          boxShadow: 'none !important'
        },
        '.Input': {
          border: '1px solid #E5E7EB !important',
          boxShadow: 'none !important',
          backgroundColor: 'transparent !important',
          padding: '12px !important'
        },
        '.Input:hover': {
          boxShadow: 'none !important',
          border: '1px solid #E5E7EB !important',
          backgroundColor: 'transparent !important'
        },
        '.Input:focus': {
          boxShadow: 'none !important',
          border: '1px solid #FF4775 !important'
        },
        '.Label': {
          fontWeight: '500',
          color: '#374151'
        },
        '.Error': {
          color: '#EF4444',
          fontSize: '14px'
        }
      },
    },
    loader: 'auto',
  };

  if (!reviewData) {
    return null;
  }

  return (
    <Box
      className="stripe-payment-wrapper"
      sx={{
        maxWidth: '600px',
        margin: '0 auto',
        p: 3,
      }}
    >
      <Paper
        className="stripe-payment-container"
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box className="stripe-payment-header" sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="h4"
              className="stripe-payment-title"
              gutterBottom
              sx={{
                fontSize: { xs: '24px', md: '28px' },
                fontWeight: 600
              }}
            >
              Complete Your Payment
            </Typography>
            
            {numLines > 1 && (
              <Chip 
                icon={<Users size={16} />} 
                label={`${numLines} Lines`}
                color="primary"
              />
            )}
          </Box>
          <Typography
            variant="body1"
            className="stripe-payment-subtitle"
            color="text.secondary"
          >
            Please enter your payment details to complete your order
            {numLines > 1 ? ` for ${numLines} lines` : ''}.
          </Typography>
        </Box>

        <Elements stripe={stripePromise} options={options}>
          <PaymentForm
            reviewData={reviewData}
            onBack={onBack}
            setLoading={setIsLoading}
          />
        </Elements>
      </Paper>
    </Box>
  );
};

export default Payment;
